import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClientService } from "@/services/ClientService";
import { ClientDialog } from "./client-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, MoreVertical, Search, Building2 } from "lucide-react";
import { useLocation } from "wouter";
import type { Client } from "@shared/schema";
import { PageHeader } from "@/components/ui/PageHeader";

export default function AdminClientsPage() {
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [, setLocation] = useLocation();

    const limit = 20;
    const offset = (page - 1) * limit;

    const { data, isLoading } = useQuery({
        queryKey: ["clients", search, page],
        queryFn: () => ClientService.getClients({ search, limit, offset }),
        placeholderData: (previousData) => previousData,
    });

    const clients = data?.data?.clients || [];
    const pagination = data?.data?.pagination;

    const handleEdit = (client: Client) => {
        setSelectedClient(client);
        setDialogOpen(true);
    };

    const handleCreate = () => {
        setSelectedClient(null);
        setDialogOpen(true);
    };

    const handleView = (client: Client) => {
        // Navigate to detail view (to be implemented)
        // For now we'll just show edit dialog until detail page is ready
        setLocation(`/admin/clients/${client.id}`);
    };

    return (
        <div className="p-6 space-y-6">
            <PageHeader
                title="Client Management"
                description="Manage commercial counterparties and their billing details."
            >
                <Button onClick={handleCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    New Client
                </Button>
            </PageHeader>

            <div className="flex items-center space-x-2">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search clients..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1); // Reset to first page on search
                        }}
                        className="pl-8"
                    />
                </div>
            </div>

            <div className="rounded-md border bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Client Name</TableHead>
                            <TableHead>Billing Name</TableHead>
                            <TableHead>Primary Contact</TableHead>
                            <TableHead>Advertisers</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-24 text-center">
                                    <div className="flex justify-center items-center">
                                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : clients.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                                    <div className="flex flex-col items-center justify-center p-4">
                                        <Building2 className="h-8 w-8 mb-2 opacity-50" />
                                        <p>No clients found</p>
                                        {search && <p className="text-sm mt-1">Try adjusting your search terms</p>}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            clients.map((client) => (
                                <TableRow key={client.id} className="cursor-pointer hover:bg-slate-50" onClick={() => handleView(client)}>
                                    <TableCell className="font-medium">
                                        <div className="flex flex-col">
                                            <span>{client.name}</span>
                                            {client.description && (
                                                <span className="text-xs text-muted-foreground truncate max-w-[200px]">{client.description}</span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>{client.billingName || "-"}</TableCell>
                                    <TableCell>
                                        <div className="flex flex-col text-sm">
                                            <span>{client.primaryContactName || "-"}</span>
                                            {client.primaryContactEmail && (
                                                <span className="text-xs text-muted-foreground">{client.primaryContactEmail}</span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            {client.advertiserCount || 0} Advertisers
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={client.isActive ? "default" : "secondary"}>
                                            {client.isActive ? "Active" : "Inactive"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" className="h-8 w-8 p-0">
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => handleView(client)}>
                                                    View Details
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleEdit(client)}>
                                                    Edit Client
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination Controls */}
            {pagination && (
                <div className="flex items-center justify-between space-x-2 py-4">
                    <div className="text-sm text-muted-foreground">
                        Showing {offset + 1}-{Math.min(offset + limit, pagination.total)} of {pagination.total} clients
                    </div>
                    <div className="space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={!pagination.hasMore}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}

            <ClientDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                client={selectedClient}
            />
        </div>
    );
}
